export interface CompetencyHierarchyNode {
  id: number;
  parentCompetencyId: number | null;
}

/**
 * In-memory parent/child competency tree built from hierarchy API nodes.
 *
 * Requirement satisfaction matches backend: held satisfies required when equal or
 * descendants(held).includes(required).
 */
export class CompetencyHierarchy {
  private readonly parentById = new Map<number, number | null>();

  private readonly childrenByParentId = new Map<number, number[]>();

  static empty(): CompetencyHierarchy {
    return new CompetencyHierarchy([]);
  }

  constructor(nodes: CompetencyHierarchyNode[]) {
    for (const node of nodes) {
      this.parentById.set(node.id, node.parentCompetencyId);
      if (node.parentCompetencyId != null) {
        const siblings = this.childrenByParentId.get(node.parentCompetencyId) ?? [];
        siblings.push(node.id);
        this.childrenByParentId.set(node.parentCompetencyId, siblings);
      }
    }
  }

  getDescendantIds(competencyId: number): number[] {
    const result: number[] = [];
    const queue = [...(this.childrenByParentId.get(competencyId) ?? [])];

    while (queue.length > 0) {
      const current = queue.shift();
      if (current == null) {
        continue;
      }
      result.push(current);
      queue.push(...(this.childrenByParentId.get(current) ?? []));
    }

    return result;
  }

  getAncestorIds(competencyId: number): number[] {
    const result: number[] = [];
    let current = this.parentById.get(competencyId) ?? null;

    while (current != null) {
      result.push(current);
      current = this.parentById.get(current) ?? null;
    }

    return result;
  }

  heldCompetencySatisfiesRequired(
    requiredCompetencyId: number,
    heldCompetencyId: number,
  ): boolean {
    if (requiredCompetencyId === heldCompetencyId) {
      return true;
    }

    return this.getDescendantIds(heldCompetencyId).includes(requiredCompetencyId);
  }
}

export function buildCompetencyHierarchy(
  competencies: Array<{ id: number | string; parentCompetencyId?: number | null }>,
): CompetencyHierarchy {
  return new CompetencyHierarchy(
    competencies.map((competency) => ({
      id: Number(competency.id),
      parentCompetencyId: competency.parentCompetencyId ?? null,
    })),
  );
}
